/* eslint-disable no-nested-ternary */
/* eslint-disable no-undef */
/* eslint-disable no-unused-expressions */

import { useState, useCallback, useMemo } from 'react';

import { showSkeleton } from '@store/common/actions';
import { getObjectFutureTransactions, getDatesEveryDayCalendar } from '@helpers';
import { getTransactions as doGetTransactions } from '@services/transactions-service';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from '@hooks/useAuth';
import { CalendarChannels } from '@constants/transactions';
import moment from 'moment';
import {
  fetchAndesBalance,
  fetchGscBalance,
  fetchMerchantBalance,
  fetchMerchantAvailable,
} from '../redux/andes/actions';

const getDateToTransactionsMonthCurrent = (date) => {
  const firstInitDate = moment(date).startOf('month').toDate();
  const endDateMonth = moment(date).endOf('month').toDate();
  if (endDateMonth.getDate() > 28) {
    const firstEndDate = new Date(date).setDate(28);
    const secondInitDate = new Date(date).setDate(29);
    return { firstInitDate, firstEndDate, secondInitDate, secondEndDate: endDateMonth };
  }
  return { firstInitDate, firstEndDate: endDateMonth };
};

const useAndes = (shouldFetchGscBalance = true) => {
  const { isSeller, isOnus } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const calendar = useMemo(() => getDatesEveryDayCalendar(currentDate), [currentDate]);
  const [channelSelected, setChannelSelected] = useState(
    isOnus ? CalendarChannels.PHYSICAL : CalendarChannels.FALABELLA
  );
  const [futureTransactions, setFutureTransactions] = useState([]);
  const [futureTransactionsError, setFutureTransactionsError] = useState();
  const [loadSettlement, setLoadSettlement] = useState(true);
  const {
    login: { merchant: loginMerchant },
    andes: { errAllBalances },
    common: { isSkeleton: isLoadingBalance },
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const {
    availableIncome,
    errGsc,
    errMerchant,
    error,
    salesInProgress,
    settledIncome,
    advanceableIncome,
    errMerchantAvailable,
    totalBalance: prevTotalBalance,
    loadedAmounts
  } = useSelector((state) => state.andes);
  const { total, physicalMerchant } = prevTotalBalance || {};
  const hasSameMerchantIdPerChannel = isOnus;
  const totalBalance = hasSameMerchantIdPerChannel ? { total, physicalMerchant } : prevTotalBalance;

  /** TODO: getBanks deberia existir en useBankAccount */
  const getBanks = useCallback(() => loginMerchant.bank_accounts, [loginMerchant]);
  const IncomeSales = useSelector((state) => state.andes.allBalance?.totals?.total_amount);

  const changeCurrentMonth = (date) => {
    setCurrentDate(date);
  };

  const getMerchantIdsByChannel = useCallback(
    () =>
      hasSameMerchantIdPerChannel
        ? {
            physical: loginMerchant.wallet_info?.merchant_id,
          }
        : {
            falabella: loginMerchant.sellers[0]?.merchant_id,
            physical: loginMerchant.wallet_info?.merchant_id,
          },
    [loginMerchant]
  );

  const getTransactionsAll = async (listBody) => {
    const perPage = 1000;
    try {
      const results = await Promise.all(
        listBody.map(({ merchantId, startDate, endDate }) =>
          doGetTransactions(getObjectFutureTransactions(merchantId, startDate, endDate, perPage))
        )
      );
      const newFutureTransactions = results.reduce(
        (allTransactions, result) => [...allTransactions, ...result.items],
        []
      );
      setFutureTransactions(newFutureTransactions);
      setFutureTransactionsError(null);
    } catch (err) {
      setFutureTransactionsError(err);
    } finally {
      setLoadSettlement(false);
    }
  };

  const getTransactionsToMonth = (merchantIds, date) => {
    const { firstInitDate, firstEndDate, secondInitDate, secondEndDate } =
      getDateToTransactionsMonthCurrent(date);

    const listBody = merchantIds.map((merchantId) => ({
      merchantId,
      startDate: firstInitDate,
      endDate: firstEndDate,
    }));
    if (!!secondInitDate && !!secondEndDate) {
      listBody.push(
        ...merchantIds.map((merchantId) => ({
          merchantId,
          startDate: secondInitDate,
          endDate: secondEndDate,
        }))
      );
    }

    getTransactionsAll(listBody);
  };

  const fetchTransactionsToMonthAndChannel = useCallback(
    (date = currentDate, channel = channelSelected) => {
      setLoadSettlement(true);
      const merchantIdsByChannel = getMerchantIdsByChannel();
      if (channel === CalendarChannels.TOTAL) {
        return getTransactionsToMonth(
          Object.values(merchantIdsByChannel).filter((values) => !!values),
          date
        );
      }
      return getTransactionsToMonth([merchantIdsByChannel[channel]], date);
    },
    [currentDate, channelSelected]
  );

  const getBalances = useCallback(
    () => (
      // eslint-disable-next-line no-sequences
      dispatch(showSkeleton()),
      dispatch(fetchAndesBalance()),
      isSeller() && shouldFetchGscBalance && dispatch(fetchGscBalance(loginMerchant.seller_id)),
      dispatch(fetchMerchantBalance()),
      dispatch(fetchMerchantAvailable(loginMerchant._id))
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loginMerchant, dispatch]
  );

  const changeChannel = useCallback(
    (channel = channelSelected) => {
      fetchTransactionsToMonthAndChannel(undefined, channel);
    },
    [fetchTransactionsToMonthAndChannel, channelSelected]
  );

  return {
    availableIncome,
    calendar,
    currentDate,
    errGsc,
    errMerchant,
    error,
    futureTransactions,
    futureTransactionsError,
    getBalances,
    getBanks,
    salesInProgress,
    settledIncome,
    advanceableIncome,
    errMerchantAvailable,
    errAllBalances,
    IncomeSales,
    totalBalance,
    changeChannel,
    channelSelected,
    setChannelSelected,
    isLoadingBalance,
    fetchTransactionsToMonthAndChannel,
    changeCurrentMonth,
    loadSettlement,
    loadedAmounts
  };
};

export default useAndes;
